/**
 * ProfilePageHeader - Header for profile pages
 * Contains logo, back button, and profile avatar
 */

import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { ArrowLeft } from 'lucide-react';

// Import assets
import logoIcon from '@/assets/logo.png';
import avatarPlaceholder from '@/assets/avatar-placeholder.png';

interface ProfilePageHeaderProps {
  /** Current user's avatar URL */
  currentUserAvatarUrl?: string;
  /** Show back button */
  showBack?: boolean;
  /** Custom back handler */
  onBack?: () => void;
}

export function ProfilePageHeader({
  currentUserAvatarUrl,
  showBack = true,
  onBack,
}: ProfilePageHeaderProps) {
  const navigate = useSafeNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/catalog');
    }
  };

  const handleLogoClick = () => {
    navigate('/catalog');
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  return (
    <header
      className="flex items-center justify-between px-4 h-[56px] bg-bg-primary"
    >
      {/* Left - Back button + Logo */}
      <div className="flex items-center gap-3">
        {showBack && (
          <button
            onClick={handleBack}
            className="w-10 h-10 rounded-full bg-bg-secondary flex items-center justify-center hover:bg-bg-secondary-pressed transition-colors"
            aria-label="Назад"
          >
            <ArrowLeft className="w-5 h-5 text-text-primary" />
          </button>
        )}
        <button
          onClick={handleLogoClick}
          className="flex items-center"
          aria-label="На главную"
        >
          <img
            src={logoIcon}
            alt="Drug"
            className="h-8 w-auto object-contain"
          />
        </button>
      </div>

      {/* Right - Profile avatar */}
      <button
        type="button"
        onClick={handleProfileClick}
        className="relative w-10 h-10 rounded-full overflow-hidden bg-bg-secondary flex-shrink-0"
        aria-label="Мой профиль"
      >
        <img
          src={currentUserAvatarUrl && currentUserAvatarUrl.trim() !== '' ? currentUserAvatarUrl : avatarPlaceholder}
          alt="Профиль"
          className="w-full h-full object-cover"
        />
      </button>
    </header>
  );
}
